<template>
  <!-- Modal Backdrop -->
  <div 
    v-if="isOpen" 
    class="fixed inset-0 z-50 overflow-y-auto"
    @click="$emit('dismiss')"
  >
    <div class="flex items-center justify-center min-h-full p-4">
      <!-- Modal Content -->
      <div 
        class="relative bg-white rounded-lg shadow-xl max-w-md w-full"
        @click.stop
      >
        <div class="p-6">
          <div class="flex items-center justify-between mb-6">
            <h3 class="text-lg font-semibold text-gray-900">Account erstellen</h3>
            <UButton
              icon="i-lucide-x"
              color="gray"
              variant="ghost"
              size="sm"
              @click="$emit('dismiss')"
            />
          </div>

          <div class="mb-4">
            <p class="text-gray-600 mb-4">
              Ihnen scheint manufix zu gefallen! Erstellen Sie jetzt ein kostenloses Konto, 
              um Ihre Projekte zu speichern und weitere Features zu nutzen.
            </p>
          </div>

      <form @submit.prevent="handleSubmit" class="space-y-4">
        <div>
          <label class="block text-sm font-medium text-gray-700 mb-1">
            Name (optional)
          </label>
          <UInput
            v-model="formData.displayName"
            placeholder="Ihr Name"
          />
        </div>

        <div>
          <label class="block text-sm font-medium text-gray-700 mb-1">
            E-Mail <span class="text-red-500">*</span>
          </label>
          <UInput
            v-model="formData.email"
            type="email"
            placeholder="ihre@email.de"
            required
          />
        </div>

        <div>
          <label class="block text-sm font-medium text-gray-700 mb-1">
            Passwort <span class="text-red-500">*</span>
          </label>
          <UInput
            v-model="formData.password"
            type="password"
            placeholder="Mindestens 6 Zeichen"
            required
          />
        </div>

        <div>
          <label class="block text-sm font-medium text-gray-700 mb-1">
            Passwort bestätigen <span class="text-red-500">*</span>
          </label>
          <UInput
            v-model="formData.confirmPassword"
            type="password"
            placeholder="Passwort wiederholen"
            required
          />
        </div>

        <div v-if="error" class="text-red-600 text-sm">
          {{ error }}
        </div>

        <div class="flex gap-3 pt-4">
          <UButton
            type="button"
            color="gray"
            variant="ghost"
            class="flex-1"
            @click="$emit('dismiss')"
          >
            Später
          </UButton>
          <UButton
            type="submit"
            color="primary"
            class="flex-1"
            :loading="loading"
          >
            Account erstellen
          </UButton>
          </div>
        </form>
        </div>
      </div>
    </div>
  </div>
  
  <!-- Backdrop overlay -->
  <div 
    v-if="isOpen"
    class="fixed inset-0 bg-black bg-opacity-50 z-40"
  ></div>
</template>

<script setup lang="ts">
interface Props {
  modelValue: boolean
}

const props = defineProps<Props>()
const emit = defineEmits<{
  'update:modelValue': [value: boolean]
  'dismiss': []
  'signup': [data: { email: string, password: string, displayName?: string }]
}>()

const isOpen = computed({
  get: () => props.modelValue,
  set: (value) => emit('update:modelValue', value)
})

const formData = ref({
  displayName: '',
  email: '',
  password: '',
  confirmPassword: ''
})

const error = ref('')
const loading = ref(false)

const handleSubmit = async () => {
  error.value = ''
  
  if (formData.value.password !== formData.value.confirmPassword) {
    error.value = 'Passwörter stimmen nicht überein'
    return
  }
  
  if (formData.value.password.length < 6) {
    error.value = 'Passwort muss mindestens 6 Zeichen haben'
    return
  }
  
  loading.value = true
  
  try {
    emit('signup', {
      email: formData.value.email,
      password: formData.value.password,
      displayName: formData.value.displayName || undefined
    })
  } catch (err) {
    error.value = 'Fehler beim Erstellen des Accounts. Bitte versuchen Sie es erneut.'
  } finally {
    loading.value = false
  }
}

// Reset form when dialog opens
watch(() => props.modelValue, (newValue) => {
  if (newValue) {
    formData.value = {
      displayName: '',
      email: '',
      password: '',
      confirmPassword: ''
    }
    error.value = ''
  }
})
</script>